import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Link from "@mui/material/Link";
import { useRecoilValue } from "recoil";
import {
  useGetItemsPriceFluctuationReport,
  useGetPriceFluctuationData,
} from "src/api/reports";
import { useGetBranches } from "src/api/generic";
import { useGetItemsList } from "src/api/purchasing/items";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportDataTable,
  reportLayoutTokens,
  ReportPageHeading,
  ReportSectionCard,
  type ReportTableColumn,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  formatNumber,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type DetailRow = {
  purchase_id: number;
  purchase_ref: string;
  branch_name: string;
  due_date: string;
  item_name: string;
  unit_name: string;
  quantity: number;
  net_total: number;
  fluctuation_percent: number;
};

function unwrapPriceRows(res: unknown): DetailRow[] {
  if (!res || typeof res !== "object") return [];
  const o = res as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object") {
    const rows = (inner as Record<string, unknown>).rows;
    if (Array.isArray(rows)) return rows as DetailRow[];
  }
  if (Array.isArray(o.rows)) return o.rows as DetailRow[];
  return [];
}

type ItemDetailReportFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
  itemId: string;
};

const parseItemDetailFilters = (sp: URLSearchParams): ItemDetailReportFilters => ({
  startDate:
    sp.get("start_date") ||
    moment().startOf("month").format("yyyy-MM-DD"),
  endDate:
    sp.get("end_date") ||
    moment().endOf("month").format("yyyy-MM-DD"),
  branchId: sp.get("branch_id") || "",
  itemId: sp.get("item_id") || "",
});

const formatReportAmount = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const formatDueDisplay = (raw: string) => {
  const m = moment(raw, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY"], true);
  if (m.isValid()) return m.format("DD/MM/YYYY");
  return raw;
};

const formatPct = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const ItemDetailReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<ItemDetailReportFilters>(() =>
    parseItemDetailFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<ItemDetailReportFilters>(() =>
    parseItemDetailFilters(new URLSearchParams(searchParams.toString()))
  );

  const [draftPurchaseRef, setDraftPurchaseRef] = useState("");
  const [draftBranchNameRow, setDraftBranchNameRow] = useState("");
  const [draftItemLineName, setDraftItemLineName] = useState("");
  const [appliedPurchaseRef, setAppliedPurchaseRef] = useState("");
  const [appliedBranchNameRow, setAppliedBranchNameRow] = useState("");
  const [appliedItemLineName, setAppliedItemLineName] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalPurchaseRefDraft, setModalPurchaseRefDraft] = useState("");
  const [modalBranchNameDraft, setModalBranchNameDraft] = useState("");
  const [modalItemLineNameDraft, setModalItemLineNameDraft] = useState("");

  const { startDate, endDate, branchId: branchIdParam, itemId: itemIdParam } =
    appliedFilters;

  const { data: branchesData } = useGetBranches();
  const { data: itemsData } = useGetItemsList();

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id)
    ) || [];

  const sortedItems = useMemo(() => {
    return [...(itemsData?.data || [])].sort((a: any, b: any) =>
      a.name?.localeCompare(b.name)
    );
  }, [itemsData]);

  const itemIdValid = itemIdParam.length > 0;
  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchFilter = Number.isFinite(branchIdNum) ? [branchIdNum] : [];

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetPriceFluctuationData({
    start_date: startDate,
    end_date: endDate,
    item_id: itemIdParam,
    branches: branchFilter,
    enabled: itemIdValid,
  });

  const { refetch: refetchPriceFluctuationReport, isFetching: isDownloadingReport } =
    useGetItemsPriceFluctuationReport({
      start_date: startDate,
      end_date: endDate,
      item_id: itemIdParam,
      branches: branchFilter,
      enabled: false,
    });

  const rows = useMemo(
    () => unwrapPriceRows(reportResponse),
    [reportResponse]
  );

  const purchaseRefOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      const x = r.purchase_ref?.trim();
      if (x) s.add(x);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [rows]);

  const branchNameRowOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      const x = r.branch_name?.trim();
      if (x) s.add(x);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [rows]);

  const itemLineNameOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      const x = r.item_name?.trim();
      if (x) s.add(x);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const pref = appliedPurchaseRef.trim();
    const br = appliedBranchNameRow.trim();
    const itemNm = appliedItemLineName.trim();
    if (!pref && !br && !itemNm) return rows;
    return rows.filter((r) => {
      if (pref && r.purchase_ref.trim() !== pref) return false;
      if (br && r.branch_name.trim() !== br) return false;
      if (itemNm && r.item_name.trim() !== itemNm) return false;
      return true;
    });
  }, [rows, appliedPurchaseRef, appliedBranchNameRow, appliedItemLineName]);

  const syncAppliedToUrl = useCallback(
    (next: ItemDetailReportFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      if (next.branchId) p.set("branch_id", next.branchId);
      if (next.itemId) p.set("item_id", next.itemId);
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const openFilterModal = () => {
    setModalPurchaseRefDraft(draftPurchaseRef);
    setModalBranchNameDraft(draftBranchNameRow);
    setModalItemLineNameDraft(draftItemLineName);
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setDraftPurchaseRef(modalPurchaseRefDraft);
    setDraftBranchNameRow(modalBranchNameDraft);
    setDraftItemLineName(modalItemLineNameDraft);
    setFilterModalOpen(false);
  };

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    setAppliedPurchaseRef(draftPurchaseRef.trim());
    setAppliedBranchNameRow(draftBranchNameRow.trim());
    setAppliedItemLineName(draftItemLineName.trim());
    syncAppliedToUrl(draftFilters);
    void refetchReport();
  };

  const downloadReportsPermission = oneBranchHasPermission(
    "download_third_eye_report"
  );

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  const handleDownload = () => {
    if (!itemIdValid) return;
    void refetchPriceFluctuationReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "price-fluctuation-report");
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const columns: ReportTableColumn<DetailRow>[] = useMemo(
    () => [
      {
        id: "purchase_ref",
        header: t("purchaseRef"),
        cell: (row) => (
          <Tooltip title={t("viewPurchase")}>
            <Link
              component={RouterLink}
              to={`/purchasing/${row.purchase_id}`}
              underline="always"
              sx={{ fontSize: "inherit" }}
            >
              {row.purchase_ref}
            </Link>
          </Tooltip>
        ),
      },
      {
        id: "branch",
        header: generalT("branch"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.branch_name}
          </Typography>
        ),
      },
      {
        id: "due",
        header: t("dueDate"),
        cell: (row) => formatDueDisplay(row.due_date),
      },
      {
        id: "item",
        header: t("itemName"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.item_name}
          </Typography>
        ),
      },
      {
        id: "unit",
        header: t("unit"),
        cell: (row) => (
          <Typography component="span" dir="auto">
            {row.unit_name}
          </Typography>
        ),
      },
      {
        id: "qty",
        header: t("quantity"),
        align: "right",
        cell: (row) => <>{formatNumber(row.quantity)}</>,
      },
      {
        id: "net",
        header: t("netTotal"),
        align: "right",
        cell: (row) => (
          <Box component="span" fontWeight={600}>
            {formatReportAmount(row.net_total)}
          </Box>
        ),
      },
      {
        id: "fluct",
        header: t("fluctuationPercent"),
        align: "right",
        cell: (row) => {
          const pct = row.fluctuation_percent;
          const color =
            pct > 0 ? "#12B76A" : pct < 0 ? "#F04438" : "#12B76A";
          return (
            <Box component="span" fontWeight={500} sx={{ color }}>
              {formatPct(pct)}%
            </Box>
          );
        },
      },
    ],
    [t, generalT]
  );

  return (
    <Box
      p={{ xs: 2, sm: "12px 32px 32px" }}
      maxWidth="100%"
    >
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#item-detail-print-root, #item-detail-print-root *": {
              visibility: "visible",
            },
            "#item-detail-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("itemDetailReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="item-detail-branch" shrink>
                  {generalT("branch")}
                </InputLabel>
                <Select
                  labelId="item-detail-branch"
                  label={generalT("branch")}
                  value={draftFilters.branchId}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    setDraftFilters((p) => ({ ...p, branchId: v }));
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t("allBranches")}</em>
                  </MenuItem>
                  {branchesHavePermission.map((branch) => (
                    <MenuItem key={branch.id} value={String(branch.id)}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={sortedItems}
                getOptionLabel={(option: { id: number; name: string }) =>
                  option.name ?? ""
                }
                value={
                  sortedItems.find(
                    (item: { id: number }) =>
                      String(item.id) === String(draftFilters.itemId)
                  ) ?? null
                }
                onChange={(_e, newValue: { id: number; name: string } | null) => {
                  const v = newValue ? String(newValue.id) : "";
                  setDraftFilters((p) => ({ ...p, itemId: v }));
                }}
                isOptionEqualToValue={(option, val) =>
                  String(option.id) === String(val.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("item")}
                    InputLabelProps={{ shrink: true }}
                    placeholder={t("selectItem")}
                  />
                )}
              />

              <Box sx={{ minWidth: 250 }}>
                <DateRangeInput
                  startDate={
                    draftFilters.startDate ? moment(draftFilters.startDate) : null
                  }
                  startDateId="item-detail-start"
                  endDate={
                    draftFilters.endDate ? moment(draftFilters.endDate) : null
                  }
                  endDateId="item-detail-end"
                  onDatesChange={(arg: {
                    startDate: moment.Moment | null;
                    endDate: moment.Moment | null;
                  }) => {
                    const s = arg.startDate?.format("yyyy-MM-DD");
                    const e = arg.endDate?.format("yyyy-MM-DD");
                    if (s && e) {
                      setDraftFilters((p) => ({
                        ...p,
                        startDate: s,
                        endDate: e,
                      }));
                    }
                  }}
                />
              </Box>
            </>
          }
          center={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={!draftFilters.itemId.trim() || isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadingReport}
                disabled={!itemIdValid || !downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("filter")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="item-detail-modal-pref" shrink>
                {t("purchaseRef")}
              </InputLabel>
              <Select<string>
                labelId="item-detail-modal-pref"
                label={t("purchaseRef")}
                value={modalPurchaseRefDraft}
                displayEmpty
                onChange={(e) =>
                  setModalPurchaseRefDraft(
                    typeof e.target.value === "string" ? e.target.value : ""
                  )
                }
                input={<OutlinedInput notched label={t("purchaseRef")} />}
                renderValue={(selected) =>
                  selected ? selected : t("purchaseRef")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{t("purchaseRef")}</em>
                </MenuItem>
                {purchaseRefOptions.map((ref) => (
                  <MenuItem key={ref} value={ref}>
                    <Box component="span" dir="auto">
                      {ref}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="item-detail-modal-branch" shrink>
                {generalT("branch")}
              </InputLabel>
              <Select<string>
                labelId="item-detail-modal-branch"
                label={generalT("branch")}
                value={modalBranchNameDraft}
                displayEmpty
                onChange={(e) =>
                  setModalBranchNameDraft(
                    typeof e.target.value === "string" ? e.target.value : ""
                  )
                }
                input={<OutlinedInput notched label={generalT("branch")} />}
                renderValue={(selected) =>
                  selected ? selected : generalT("branch")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{generalT("branch")}</em>
                </MenuItem>
                {branchNameRowOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Box component="span" dir="auto">
                      {name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="item-detail-modal-itemline" shrink>
                {t("itemName")}
              </InputLabel>
              <Select<string>
                labelId="item-detail-modal-itemline"
                label={t("itemName")}
                value={modalItemLineNameDraft}
                displayEmpty
                onChange={(e) =>
                  setModalItemLineNameDraft(
                    typeof e.target.value === "string" ? e.target.value : ""
                  )
                }
                input={<OutlinedInput notched label={t("itemName")} />}
                renderValue={(selected) =>
                  selected ? selected : t("itemName")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{t("itemName")}</em>
                </MenuItem>
                {itemLineNameOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Box component="span" dir="auto">
                      {name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="item-detail-print-root">
        <ReportPageHeading
          title={t("itemDetailReport")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {!itemIdValid ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                {t("selectItemToViewReport")}
              </Typography>
            </Box>
          ) : (
            <ReportDataTable<DetailRow>
              columns={columns}
              rows={filteredRows}
              getRowId={(row, i) => `${row.purchase_ref}-${i}`}
              isLoading={isFetching}
              emptyMessage={
                <Typography color="text.secondary" align="center" py={2}>
                  {generalT("noData")}
                </Typography>
              }
            />
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default ItemDetailReportPage;
